import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import useManualSwap from "../../manualSwapLatest/hooks/use-manual-swap";
import { Alert } from "@mui/material";
import { CheckCircle, Clock, Settings } from "lucide-react";
import { CommonStepTwo } from "../common";
import SwapStatusCard from "./SwapStatusCard";
import { LoaderButton } from "../../mui";
import { endpoints } from "../../../utils/endpoints";
import { useStepperContext } from "../StepperContext";
import SwapProgress from "./SwapProgress";
import {
  EXPECTED_SWAP_STATUS,
  SWAP_PROGRESS_STATUS,
} from "../../../constants/swapData";
import SwapInfoSection from "./SwapInfoSection";

const POLLING_INTERVAL = 8000; // 8 seconds
const POLLING_TIMEOUT = 240000; // 4 minutes

const initialProgressSteps = [
  { id: 1, name: "Submitting Request", status: "pending", icon: Clock },
  { id: 2, name: "Processing Swap", status: "pending", icon: Settings },
  { id: 3, name: "Complete", status: "pending", icon: CheckCircle },
];

export default function OtaSwap({ swapType }) {
  const [progressSteps, setProgressSteps] = useState(initialProgressSteps);
  const [isSwapTimeout, setIsSwapTimeout] = useState(false);
  const [swapStatus, setSwapStatus] = useState("");
  const [swapTransactionData, setSwapTransactionData] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [pollingIntervalId, setPollingIntervalId] = useState(null);

  const { data, updateData, handleReset } = useStepperContext();
  const { isLoading, errorMsg, onSubmitManualSwap } = useManualSwap();

  const resetProgress = () => {
    setSwapStatus("");
    setIsSwapTimeout(false);
    setProgressSteps(initialProgressSteps);
    setSwapTransactionData(null);

    // Clear any existing polling
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      setPollingIntervalId(null);
    }
  };

  const updateStepStatus = (stepIndex, status) => {
    setProgressSteps((prev) =>
      prev.map((step, idx) => (idx === stepIndex ? { ...step, status } : step))
    );
  };

  const handleSubmit = async () => {
    resetProgress();
    updateData("swapInitiated", true);
    updateStepStatus(0, "loading");

    const payload = {
      deviceId: data.deviceId,
      partnerId: data.partnerId,
      driverId: data.driverId,
      manualSwapType: swapType,
    };

    try {
      setSwapStatus(SWAP_PROGRESS_STATUS.SWAP_IN_PROGRESS);
      const response = await onSubmitManualSwap(payload);

      updateStepStatus(0, "completed");
      updateStepStatus(1, "loading");

      if (response?.transactionId) {
        setTransactionId(response.transactionId);
        await startPolling(response.transactionId);
      } else {
        throw new Error("No transaction ID received from manual swap response");
      }
    } catch (error) {
      console.error("Manual swap failed:", error);
      updateStepStatus(0, "error");
      setSwapStatus(SWAP_PROGRESS_STATUS.ERROR);
    }
  };

  const startPolling = async (transactionId) => {
    const pollUrl = `${endpoints.manualSwap.getSwapLogsBaas}?transactionId=${transactionId}`;
    let hasReceivedFinalStatus = false;

    const intervalId = setInterval(async () => {
      try {
        const res = await fetch(pollUrl);
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        const swapData = data?.data?.length > 0 ? data?.data[0] : {};

        if (swapData?.swapStatus) {
          setSwapTransactionData({ ...swapData, transactionId });
          setSwapStatus(swapData.swapStatus);
          updateData("swapStatus", swapData.swapStatus);

          // Update step 2 to completed when we get data
          updateStepStatus(1, "completed");
          updateStepStatus(2, "loading");

          // Check if swap is in final state (including swap_incomplete)
          if (EXPECTED_SWAP_STATUS.includes(swapData.swapStatus)) {
            hasReceivedFinalStatus = true;
            clearInterval(intervalId);
            setPollingIntervalId(null);
            updateStepStatus(2, "completed");
          }
        }
      } catch (error) {
        console.error("Polling error:", error);
        clearInterval(intervalId);
        setPollingIntervalId(null);
        updateStepStatus(1, "error");
        setSwapStatus(SWAP_PROGRESS_STATUS.ERROR);
      }
    }, POLLING_INTERVAL);

    setPollingIntervalId(intervalId);

    // Set timeout - only apply if we haven't received a final status from API
    setTimeout(() => {
      if (intervalId && !hasReceivedFinalStatus) {
        clearInterval(intervalId);
        setPollingIntervalId(null);
        setIsSwapTimeout(true);
        updateStepStatus(2, "error");
      }
    }, POLLING_TIMEOUT);
  };

  const getInputValidation = () => {
    return (
      !data.partnerId ||
      !data.driverId ||
      !data.deviceId ||
      !data.isDriverDetails
    );
  };

  const handleSwapAgain = () => {
    resetProgress();
    handleReset();
  };

  const isSwapInProgress =
    swapStatus && !EXPECTED_SWAP_STATUS.includes(swapStatus);
  const isSwapCompleted = EXPECTED_SWAP_STATUS.includes(swapStatus);
  const showSwapProgress = data.swapInitiated && swapStatus;

  return (
    <Box>
      <CommonStepTwo />

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <LoaderButton
          variant="contained"
          onClick={handleSubmit}
          isLoading={isLoading || isSwapInProgress}
          disabled={getInputValidation() || data.swapInitiated}
          sx={{ width: "150px" }}
        >
          Swap Initiate
        </LoaderButton>
      </Box>

      {errorMsg && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMsg}
        </Alert>
      )}

      {showSwapProgress && (
        <>
          <SwapProgress
            progressSteps={progressSteps}
            swapStatus={swapStatus}
            isSwapTimeout={isSwapTimeout}
          />
          <SwapStatusCard
            swapTransactionData={swapTransactionData}
            swapStatus={swapStatus}
            isSwapTimeout={isSwapTimeout}
          />
        </>
      )}

      {swapStatus === SWAP_PROGRESS_STATUS.SWAP_COMPLETED && (
        <SwapInfoSection
          data={swapTransactionData}
          transactionId={transactionId}
        />
      )}

      {isSwapCompleted && (
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={handleSwapAgain}
            sx={{ width: "150px" }}
          >
            Swap Again
          </Button>
        </Box>
      )}
    </Box>
  );
}
